import { Share } from '@capacitor/share';
import { Capacitor } from '@capacitor/core';
import { message } from 'antd';

export interface ShareOptions {
  roomId: string;
  title?: string;
  text?: string;
}

export const shareRoom = async (options: ShareOptions): Promise<void> => {
  const { roomId } = options;
  
  // Get translated strings - fallback to English if not found
  const getCurrentLanguage = () => {
    try {
      return localStorage.getItem('i18nextLng') || 'en';
    } catch {
      return 'en';
    }
  };
  
  const lang = getCurrentLanguage();
  const isEnglish = lang === 'en';
  
  const title = isEnglish 
    ? 'Join my Game Garden - King of Hearts!'
    : 'Game Garden - King oyununa katıl!';
  const text = isEnglish 
    ? 'Click the link to join my game room:'
    : 'Oyun odama katılmak için linke tıkla:';
  const dialogTitle = isEnglish 
    ? 'Share Game Room'
    : 'Oyun Odasını Paylaş';
  
  // Construct the share URL - use production URL for sharing
  const baseUrl = Capacitor.isNativePlatform() 
    ? 'https://www.gamegarden.co'  // Always use production URL for mobile sharing
    : window.location.origin;      // Use current origin for desktop
  const shareUrl = `${baseUrl}?room=${encodeURIComponent(roomId)}`;
  
  try {
    if (Capacitor.isNativePlatform()) {
      // Mobile: Use native sharing
      await Share.share({
        title: title,
        text: text,
        url: shareUrl,
        dialogTitle: dialogTitle
      });
    } else {
      // Desktop: Copy to clipboard
      if (navigator.clipboard && window.isSecureContext) {
        // Modern clipboard API (requires HTTPS)
        await navigator.clipboard.writeText(shareUrl);
        message.success(isEnglish ? 'Room link copied to clipboard!' : 'Oda linki panoya kopyalandı!');
      } else {
        // Fallback for older browsers or HTTP
        copyToClipboardFallback(shareUrl);
        message.success(isEnglish ? 'Room link copied to clipboard!' : 'Oda linki panoya kopyalandı!');
      }
    }
  } catch (error: any) {
    console.error('Error sharing room:', error);
    
    // If native share fails on mobile, fallback to clipboard
    if (Capacitor.isNativePlatform()) {
      try {
        if (navigator.clipboard && window.isSecureContext) {
          await navigator.clipboard.writeText(shareUrl);
          message.success(isEnglish ? 'Room link copied to clipboard!' : 'Oda linki panoya kopyalandı!');
        } else {
          copyToClipboardFallback(shareUrl);
          message.success(isEnglish ? 'Room link copied to clipboard!' : 'Oda linki panoya kopyalandı!');
        }
      } catch (clipboardError) {
        console.error('Clipboard fallback failed:', clipboardError);
        const errorMsg = isEnglish 
          ? 'Unable to share room link. Please share manually: '
          : 'Oda linki paylaşılamıyor. Lütfen manuel olarak paylaş: ';
        message.error(errorMsg + shareUrl);
      }
    } else {
      message.error(isEnglish ? 'Unable to copy link to clipboard' : 'Link panoya kopyalanamadı');
    }
  }
};

// Fallback clipboard function for older browsers
const copyToClipboardFallback = (text: string): void => {
  const textArea = document.createElement('textarea');
  textArea.value = text;
  
  // Avoid scrolling to bottom
  textArea.style.top = '0';
  textArea.style.left = '0';
  textArea.style.position = 'fixed';
  textArea.style.opacity = '0';
  
  document.body.appendChild(textArea);
  textArea.focus();
  textArea.select();
  
  try {
    const successful = document.execCommand('copy');
    if (!successful) {
      throw new Error('execCommand failed');
    }
  } catch (err) {
    console.error('Fallback clipboard copy failed:', err);
    throw err;
  }
  
  document.body.removeChild(textArea);
};

// Helper function to get current room ID from URL params, Redux store, or window
export const getCurrentRoomId = (): string | null => {
  try {
    // First try to get from URL parameters (for desktop sharing)
    if (!Capacitor.isNativePlatform()) {
      const urlParams = new URLSearchParams(window.location.search);
      const roomFromUrl = urlParams.get('room') || urlParams.get('roomId');
      if (roomFromUrl) {
        return roomFromUrl;
      }
    }
    
    // Fallback to Redux store or window global
    const state = (window as any).store?.getState();
    return state?.kingGame?.gameState?.room || (window as any).currentRoom || null;
  } catch (error) {
    console.error('Error getting current room ID:', error);
    return null;
  }
};

// Check if sharing is available
export const isSharingAvailable = (): boolean => {
  if (Capacitor.isNativePlatform()) {
    // Native sharing is usually available on mobile
    return true;
  } else {
    // Desktop: Check if clipboard is available
    return !!(navigator.clipboard || document.execCommand);
  }
};